/**
 * Praten met de server over je account.
 *
 * Alles loopt via `fetch` naar dezelfde oorsprong, dus de sessiecookie gaat vanzelf mee —
 * geen tokens die het scherm moet bewaren, en JavaScript komt niet bij de cookie. Dat is
 * met opzet: een lek in het scherm mag geen sessie opleveren.
 *
 * Twee dingen die dit bestand goed moet doen en die je makkelijk vergeet. Ten eerste:
 * antwoorden van de server worden hier ook aan het schema gehouden. Ze komen van onze
 * eigen server, maar tussen die twee zit een netwerk en een cache, en een halve JSON die
 * je klakkeloos gelooft levert een scherm op dat "undefined" zegt.
 *
 * Ten tweede: er is een verschil tussen "je bent niet ingelogd" en "de server is er niet".
 * Het eerste is een gewoon antwoord, het tweede iets waar de speler niets aan kan doen. In
 * de bouwomgeving draait vaak alleen de webapp zonder server; dan hoort het scherm gewoon
 * te werken met de mededeling dat inloggen nu niet kan.
 */
import { FoutSchema, SessieSchema, WieBenIkSchema, type Foutcode, type Ik } from '@catan/contracts';

/** Wat een verzoek kan opleveren. */
export type Uitkomst<T> =
  | { readonly ok: true; readonly waarde: T }
  | { readonly ok: false; readonly code: Foutcode | 'GEEN_SERVER'; readonly bericht: string; readonly wachtSeconden?: number };

async function roep(pad: string, lichaam?: unknown): Promise<Uitkomst<Ik>> {
  let res: Response;
  try {
    res = await fetch(pad, {
      method: lichaam === undefined ? 'GET' : 'POST',
      ...(lichaam === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(lichaam) }),
      // Zelfde oorsprong, dus de cookie gaat mee. Expliciet, want de standaard verschilt
      // per browser en dit is te belangrijk om aan de standaard over te laten.
      credentials: 'same-origin',
    });
  } catch {
    return { ok: false, code: 'GEEN_SERVER', bericht: 'geen verbinding met de server' };
  }

  let ontleed: unknown;
  try {
    ontleed = await res.json();
  } catch {
    return { ok: false, code: 'GEEN_SERVER', bericht: 'de server antwoordde geen JSON' };
  }

  if (res.ok) {
    const sessie = SessieSchema.safeParse(ontleed);
    if (!sessie.success) {
      return { ok: false, code: 'SERVERFOUT', bericht: 'onbegrijpelijk antwoord van de server' };
    }
    return { ok: true, waarde: sessie.data.ik };
  }

  const fout = FoutSchema.safeParse(ontleed);
  if (!fout.success) {
    return { ok: false, code: 'SERVERFOUT', bericht: `de server gaf ${res.status}` };
  }
  return {
    ok: false,
    code: fout.data.fout,
    bericht: fout.data.bericht,
    ...(fout.data.wachtSeconden === undefined ? {} : { wachtSeconden: fout.data.wachtSeconden }),
  };
}

/**
 * Wie ben ik? `undefined` als je niet ingelogd bent of er geen server is.
 *
 * Dit pad antwoordt met 200 en `ik: null` als er niemand is, dus het gaat langs `roep`
 * heen: die verwacht een sessie of een fout, en "niemand" is geen van beide.
 */
export async function wieBenIk(): Promise<Ik | undefined> {
  const uit = await wieBenIkPreciezer();
  return uit === 'geen-server' ? undefined : uit;
}

/**
 * Zoals `wieBenIk`, maar met het verschil tussen "niemand" en "geen server".
 *
 * De voordeur heeft dat verschil nodig: bij niemand hoort een inlogformulier, bij geen
 * server een mededeling. Vroeger stond daar een aparte `/health`-peiling naast — twee
 * verzoeken voor één vraag, en de tweede werd afgebroken zodra de pagina navigeerde, wat
 * elke toets een "mislukt verzoek" in de console opleverde.
 */
export async function wieBenIkPreciezer(): Promise<Ik | undefined | 'geen-server'> {
  try {
    const res = await fetch('/api/ik', { credentials: 'same-origin' });
    if (!res.ok) return res.status >= 500 ? 'geen-server' : undefined;
    const uit = WieBenIkSchema.safeParse(await res.json());
    return uit.success ? (uit.data.ik ?? undefined) : undefined;
  } catch {
    return 'geen-server';
  }
}

export const registreren = (code: string, gebruikersnaam: string, wachtwoord: string) =>
  roep('/api/registreren', { code: code.toUpperCase(), gebruikersnaam, wachtwoord });

export const inloggen = (gebruikersnaam: string, wachtwoord: string) =>
  roep('/api/inloggen', { gebruikersnaam, wachtwoord });

export async function uitloggen(): Promise<void> {
  try {
    await fetch('/api/uitloggen', { method: 'POST', credentials: 'same-origin' });
  } catch {
    // Lukt het uitloggen niet, dan is de server weg — en dan is er ook niemand die je
    // sessie nog gebruikt. Het scherm vergeet je hoe dan ook.
  }
}

/** Is er überhaupt een server om mee te praten? */
export async function serverBereikbaar(): Promise<boolean> {
  try {
    const res = await fetch('/health', { credentials: 'same-origin' });
    return res.ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- potjes

/** Een oefenpotje op de server beginnen. Geeft het id terug, of niets als het niet lukte. */
export async function maakOefenpotje(keuze: {
  tegenstander: string;
  figuur: string;
  moeilijkheid: string;
  zelfVerdelen: boolean;
  /** Een vast lot; alleen gevuld als de URL erom vraagt (`?seed=`). */
  seed?: string | undefined;
}): Promise<string | undefined> {
  try {
    const res = await fetch('/api/potjes', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(keuze),
    });
    if (!res.ok) return undefined;
    const uit = (await res.json()) as { id?: string };
    return typeof uit.id === 'string' ? uit.id : undefined;
  } catch {
    return undefined;
  }
}

/** De potjes die nog lopen, nieuwste eerst. */
export interface LopendPotje {
  readonly id: string;
  readonly tegenstander: string;
  /** Tegen de computer, of tegen een mens? Het scherm zegt het erbij in de kiezer. */
  readonly tegenComputer?: boolean;
}

export async function lopendePotjes(): Promise<LopendPotje[]> {
  try {
    const res = await fetch('/api/potjes', { credentials: 'same-origin' });
    if (!res.ok) return [];
    const uit = (await res.json()) as { potjes?: LopendPotje[] };
    return uit.potjes ?? [];
  } catch {
    return [];
  }
}
